import React, { useEffect, useRef, useState } from "react";

export function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [variant, setVariant] = useState("default");

  useEffect(() => {
    let mx = window.innerWidth / 2, my = window.innerHeight / 2;
    let rx = mx, ry = my;
    let raf;

    const onMove = (e) => {
      mx = e.clientX; my = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${mx}px, ${my}px) translate(-50%, -50%)`;
      }
    };
    const tick = () => {
      rx += (mx - rx) * 0.18;
      ry += (my - ry) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${rx}px, ${ry}px) translate(-50%, -50%)`;
      }
      raf = requestAnimationFrame(tick);
    };
    tick();

    const onOver = (e) => {
      const t = e.target;
      if (t.closest && t.closest('[data-cursor="text"]')) setVariant("text");
      else if (t.closest && t.closest('a, button, [data-cursor="hover"]')) setVariant("hover");
      else setVariant("default");
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <div ref={dotRef} className="cursor-dot" />
      <div
        ref={ringRef}
        className={"cursor-ring" + (variant === "hover" ? " hovering" : variant === "text" ? " text" : "")}
      />
    </>
  );
}

export function ScrollProgress() {
  const ref = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      const s = document.documentElement.scrollHeight - window.innerHeight;
      const p = s > 0 ? (window.scrollY / s) : 0;
      if (ref.current) ref.current.style.transform = `scaleX(${p})`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return <div className="scroll-progress" ref={ref} />;
}

export function Aurora() {
  return (
    <div className="aurora" aria-hidden="true">
      <div className="aurora-blob ab1" />
      <div className="aurora-blob ab2" />
      <div className="aurora-blob ab3" />
      <div className="aurora-blob ab4" />
    </div>
  );
}

export function MeshBackdrop() {
  return <div className="mesh-bg" aria-hidden="true" />;
}

export function Spotlight() {
  const ref = useRef(null);
  useEffect(() => {
    const onMove = (e) => {
      if (!ref.current) return;
      ref.current.style.setProperty("--mx", e.clientX + "px");
      ref.current.style.setProperty("--my", e.clientY + "px");
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);
  return <div className="spotlight" ref={ref} aria-hidden="true" />;
}

export function MatchToasts() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    const samples = [
      { a: "Anjali", b: "Arun",   city: "Kochi" },
      { a: "Maya",   b: "Theo",   city: "Bengaluru" },
      { a: "Iris",   b: "Noah",   city: "Lisbon" },
      { a: "Zara",   b: "Kai",    city: "Mumbai" },
      { a: "Mira",   b: "Leo",    city: "Chennai" },
    ];
    let i = 0;
    const id = setInterval(() => {
      const sample = samples[i % samples.length];
      const tid = Math.random();
      setToasts((t) => [...t, { ...sample, id: tid }]);
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== tid)), 4500);
      i++;
    }, 3500);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="match-toasts">
      {toasts.map((t) => (
        <div key={t.id} className="match-toast">
          <div className="match-toast-avatars">
            <span style={{ background: "linear-gradient(135deg, oklch(0.78 0.12 230), oklch(0.55 0.18 250))" }}>{t.a[0]}</span>
            <span style={{ background: "linear-gradient(135deg, oklch(0.78 0.1 80), oklch(0.55 0.18 60))" }}>{t.b[0]}</span>
          </div>
          <div>
            <div className="match-toast-title"><b>{t.a}</b> &amp; <b>{t.b}</b> matched</div>
            <div className="match-toast-meta">{t.city} · just now</div>
          </div>
          <span className="match-toast-heart">&#9829;</span>
        </div>
      ))}
    </div>
  );
}

export function Magnetic({ children, strength = 0.3 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      el.style.transform = `translate(${dx * strength}px, ${dy * strength}px)`;
    };
    const reset = () => { el.style.transform = ""; };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", reset);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", reset);
    };
  }, [strength]);
  return (
    <span
      ref={ref}
      style={{
        display: "inline-block",
        willChange: "transform",
        transition: "transform .25s cubic-bezier(.2,.8,.2,1)",
      }}
    >
      {children}
    </span>
  );
}

export function ParticleField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = 0, h = 0;
    const dpr = window.devicePixelRatio || 1;
    let raf;
    const mouse = { x: -1000, y: -1000, active: false };
    const N = Math.min(120, Math.floor(window.innerWidth / 14));
    const dots = [];

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const init = () => {
      dots.length = 0;
      for (let i = 0; i < N; i++) {
        dots.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.25,
          vy: (Math.random() - 0.5) * 0.25,
          r: Math.random() * 1.6 + 0.6,
        });
      }
    };

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      const dotColor = "rgba(60,90,180,0.7)";
      const lineBase = "rgba(60,90,180,";

      for (const d of dots) {
        if (mouse.active) {
          const dx = mouse.x - d.x, dy = mouse.y - d.y;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < 24000) {
            const f = (1 - dist2 / 24000) * 0.06;
            d.vx += dx * f * 0.01;
            d.vy += dy * f * 0.01;
          }
        }
        d.vx *= 0.98; d.vy *= 0.98;
        d.x += d.vx; d.y += d.vy;
        if (d.x < 0) d.x = w; else if (d.x > w) d.x = 0;
        if (d.y < 0) d.y = h; else if (d.y > h) d.y = 0;
      }

      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const a = dots[i], b = dots[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 14000) {
            const o = (1 - d2 / 14000) * 0.45;
            ctx.strokeStyle = lineBase + o + ")";
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      for (const d of dots) {
        ctx.fillStyle = dotColor;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fill();
      }

      if (mouse.active) {
        const g = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 180);
        g.addColorStop(0, "rgba(80,130,240,0.18)");
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 180, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(tick);
    };

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
      mouse.active = true;
    };
    const onLeave = () => { mouse.active = false; };
    const onResize = () => { resize(); init(); };

    resize(); init(); tick();
    window.addEventListener("resize", onResize);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseout", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);
  return <canvas ref={canvasRef} className="particle-field" />;
}

export function LoveSticker() {
  const ref = useRef(null);
  const [pct, setPct] = useState(96);
  const target = useRef({ x: 0, y: 0 });
  const pos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e) => {
      const host = document.querySelector(".hero-visual");
      if (!host) return;
      const r = host.getBoundingClientRect();
      target.current.x = e.clientX - r.left;
      target.current.y = e.clientY - r.top;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  useEffect(() => {
    let raf;
    const tick = () => {
      pos.current.x += (target.current.x - pos.current.x) * 0.06;
      pos.current.y += (target.current.y - pos.current.y) * 0.06;
      if (ref.current) {
        ref.current.style.transform = `translate(${pos.current.x - 60}px, ${pos.current.y - 60}px)`;
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setPct((p) => Math.max(82, Math.min(99, p + (Math.random() - 0.5) * 4)));
    }, 1400);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="love-sticker" ref={ref} aria-hidden="true">
      <svg viewBox="0 0 120 120" width="120" height="120">
        <defs>
          <path id="lscircle" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" />
        </defs>
        <circle cx="60" cy="60" r="54"
                fill="none" stroke="var(--ink)" strokeWidth="1" strokeDasharray="2 4" />
        <text fontSize="9" letterSpacing="2" fontFamily="var(--mono)" fill="var(--ink)">
          <textPath xlinkHref="#lscircle">
            HRIDHAYAM &middot; LOVE-O-METER &middot; HRIDHAYAM &middot; LOVE-O-METER &middot;&nbsp;
          </textPath>
        </text>
        <g transform="translate(60 60)">
          <circle r="26" fill="var(--blue-deep)" />
          <text textAnchor="middle" y="3" fontFamily="var(--serif)" fontStyle="italic" fontSize="22" fill="var(--cream)">
            {Math.round(pct)}%
          </text>
        </g>
      </svg>
    </div>
  );
}

export function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("in"); obs.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

export function useHeartTrail() {
  useEffect(() => {
    const onClick = (e) => {
      const el = document.createElement("div");
      el.className = "heart-trail";
      el.textContent = "♥";
      el.style.left = e.clientX + "px";
      el.style.top = e.clientY + "px";
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 1200);
    };
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, []);
}
